import { Skeleton as KitSkeleton } from '@telegram-apps/telegram-ui';

import styles from './Skeleton.module.css';

/**
 * Skeleton-кит miniapp (Веха 14.x): мерцающие «призраки» контента на время
 * загрузки — вместо `Placeholder` + `Spinner` + текста «Загрузка…».
 *
 * Поверх kit `Skeleton` (visible): он сам берёт цвет заливки и пульсацию из
 * токенов темы Telegram (--tgui--secondary_bg_color / --tgui--bg_color), поэтому
 * цвет тут не задаём (CANON F2). Форму даёт класс из Skeleton.module.css.
 *
 * Идиома kit: один `<Skeleton visible>` рисует ОДИН пульсирующий прямоугольник по
 * своему bounding-box. «Призрак» строки (кружок + 2 черты + хвост) — это
 * несколько таких брусков, разложенных flex'ом.
 */

/** Один мерцающий брусок-заглушка. Форма — через className (см. CSS). */
function Skel({ className }: { className?: string }) {
  return <KitSkeleton visible className={className ? `${styles.box} ${className}` : styles.box} />;
}

const cx = (...c: Array<string | false | undefined>) => c.filter(Boolean).join(' ');

/**
 * N строк-«призраков» списка: ведущий кружок (ранг/аватар) + две черты текста +
 * хвост (счёт/переключатель). Лидерборд, настройки, «загрузить ещё».
 */
export function SkeletonRows({
  count,
  withLead = true,
  withTrail = true,
}: {
  count: number;
  withLead?: boolean;
  withTrail?: boolean;
}) {
  return (
    <div className={styles.rows} aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={styles.row}>
          {withLead && <Skel className={styles.circle} />}
          <div className={styles.rowText}>
            <Skel className={cx(styles.line, styles.w55)} />
            <Skel className={cx(styles.lineCaption, styles.w40)} />
          </div>
          {withTrail && <Skel className={styles.rowTrail} />}
        </div>
      ))}
    </div>
  );
}

/** AskScreen: заголовок-промпт + подсказка, затем карточка поля ввода. */
export function AskSkeleton() {
  return (
    <div aria-hidden>
      <div className={styles.askHead}>
        <Skel className={cx(styles.lineTitle, styles.w70)} />
        <Skel className={cx(styles.lineCaption, styles.w40)} />
      </div>
      <Skel className={styles.field} />
    </div>
  );
}

/** RewardsScreen: иконка уровня + номер + подпись + прогресс-бар по центру. */
export function RewardsSkeleton() {
  return (
    <div className={styles.rewards} aria-hidden>
      <Skel className={styles.circleLg} />
      <Skel className={cx(styles.lineTitle, styles.w40)} />
      <Skel className={cx(styles.lineCaption, styles.w55)} />
      <div className={styles.progressWrap}>
        <Skel className={cx(styles.bar, styles.w90)} />
      </div>
    </div>
  );
}

/** InboxScreen: блок-приглашение сверху + несколько пузырей-вопросов. */
export function InboxSkeleton() {
  return (
    <div className={styles.inbox} aria-hidden>
      <Skel className={cx(styles.block, styles.invite)} />
      <div className={styles.bubbles}>
        <Skel className={cx(styles.block, styles.bubble, styles.w70)} />
        <Skel className={cx(styles.block, styles.bubble, styles.w90)} />
        <Skel className={cx(styles.block, styles.bubble, styles.w55)} />
      </div>
    </div>
  );
}

/** Один пузырь-«призрак» — для «загрузить ещё» во входящих. */
export function InboxBubbleSkeleton() {
  return (
    <div className={styles.bubbles} aria-hidden>
      <Skel className={cx(styles.block, styles.bubble, styles.w70)} />
    </div>
  );
}
